import { API_BASE } from '../env';
import { apiFetch } from '../auth/apiClient';

// ── Request / Response types ─────────────────────────────────

export interface CreateBacktestSessionRequest {
  startDate: string;   // "YYYY-MM-DD"
  endDate: string;     // "YYYY-MM-DD"
  startTime?: string;  // "HH:mm" (e.g. "09:30"), omit for start of day
  endTime?: string;    // "HH:mm" (e.g. "16:00"), omit for end of day
  symbols: string[];
  playbackSpeed?: number;   // default 1 (real-time)
  dataType?: string;        // "minute" | "daily"
  timeframe?: string;       // "1M" | "5M" | "1H" | "1D"
  scannerIds?: string[];
  alertCodes?: string[];
  /** Scanner-strategy IDs (analysis presets like MomentumStrategy)
   * to evaluate against bars. Distinct from `strategyRuleIds`. */
  /** User-defined Strategy doc IDs to run for paper trading during
   * this backtest. Was `automationRuleIds` pre-slice-3. */
  strategyRuleIds?: string[];
}

export interface BacktestSessionStatus {
  sessionId: string;
  status: string;
  startedAt?: string;
  completedAt?: string;
  currentPlaybackTime: string;
  barsProcessed: number;
  totalBarsToProcess: number;
  percentComplete: number;
  subscribedSymbols: string[];
  error?: string;
  config?: CreateBacktestSessionRequest;
}

export interface BacktestClockStatus {
  sessionId: string;
  currentTimeUtc: string;
  currentTimeEt: string;
  marketSession: string;
  isPreMarket: boolean;
  isRegularHours: boolean;
  isAfterHours: boolean;
  formattedTimeEt: string;
}

export interface BacktestClockTick {
  sessionId: string;
  currentTimeUtc: string;
  currentTimeEt: string;
  session: string;
  speed: number;
  isPaused: boolean;
  formattedTimeEt: string;
}

// ── Service ──────────────────────────────────────────────────

class BacktestService {
  private baseUrl = `${API_BASE}/Backtest`;

  /** Create a new backtest session (does NOT start it). Returns sessionId. */
  async createSession(request: CreateBacktestSessionRequest): Promise<{ sessionId: string }> {
    const res = await apiFetch(`${this.baseUrl}/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!res.ok) throw new Error(`Failed to create session: ${await res.text()}`);
    return res.json();
  }

  /** Start playback for a session. */
  async startSession(sessionId: string): Promise<void> {
    const res = await apiFetch(`${this.baseUrl}/session/${sessionId}/start`, { method: 'POST' });
    if (!res.ok) throw new Error(`Failed to start session: ${await res.text()}`);
  }

  /** Pause a running session. */
  async pauseSession(sessionId: string): Promise<void> {
    const res = await apiFetch(`${this.baseUrl}/session/${sessionId}/pause`, { method: 'POST' });
    if (!res.ok) throw new Error(`Failed to pause session: ${await res.text()}`);
  }

  /** Resume a paused session. */
  async resumeSession(sessionId: string): Promise<void> {
    const res = await apiFetch(`${this.baseUrl}/session/${sessionId}/resume`, { method: 'POST' });
    if (!res.ok) throw new Error(`Failed to resume session: ${await res.text()}`);
  }

  /** Stop and clean up a session. */
  async stopSession(sessionId: string): Promise<void> {
    const res = await apiFetch(`${this.baseUrl}/session/${sessionId}/stop`, { method: 'POST' });
    if (!res.ok) throw new Error(`Failed to stop session: ${await res.text()}`);
  }

  /** Change playback speed. */
  async setSpeed(sessionId: string, speed: number): Promise<void> {
    const res = await apiFetch(`${this.baseUrl}/session/${sessionId}/speed?speed=${speed}`, { method: 'POST' });
    if (!res.ok) throw new Error(`Failed to set speed: ${await res.text()}`);
  }

  /** Get session status and progress. */
  async getStatus(sessionId: string): Promise<BacktestSessionStatus> {
    const res = await apiFetch(`${this.baseUrl}/session/${sessionId}/status`);
    if (!res.ok) throw new Error(`Failed to get status: ${await res.text()}`);
    return res.json();
  }

  /** Get the simulated clock status. */
  async getClockStatus(sessionId: string): Promise<BacktestClockStatus> {
    const res = await apiFetch(`${this.baseUrl}/session/${sessionId}/clock`);
    if (!res.ok) throw new Error(`Failed to get clock: ${await res.text()}`);
    return res.json();
  }

  /** Enable scanners for a running session. */
  async enableScanners(sessionId: string, scannerIds: string[]): Promise<void> {
    const res = await apiFetch(`${this.baseUrl}/session/${sessionId}/scanners`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(scannerIds),
    });
    if (!res.ok) throw new Error(`Failed to enable scanners: ${await res.text()}`);
  }

  /** Enable alerts for a running session. */
  async enableAlerts(sessionId: string, alertCodes: string[]): Promise<void> {
    const res = await apiFetch(`${this.baseUrl}/session/${sessionId}/alerts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(alertCodes),
    });
    if (!res.ok) throw new Error(`Failed to enable alerts: ${await res.text()}`);
  }

  /** List all active sessions. */
  async listSessions(): Promise<{ count: number; sessions: string[] }> {
    const res = await apiFetch(`${this.baseUrl}/sessions`);
    if (!res.ok) throw new Error(`Failed to list sessions: ${await res.text()}`);
    return res.json();
  }
}

export const backtestService = new BacktestService();
export default backtestService;
